import { Component, DestroyRef, inject, Input, OnInit, signal } from '@angular/core';
import { LayerData, RiskReport } from '../../models/report.model';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { ShareModalComponent } from '../../components/share-modal/share-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../../components/confirmation-dialog/confirmation-dialog.component';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ToastNotificationService } from '../../services/toast-notification.service';
import { NotificationService } from '../../services/notification.service';
import { getRiskColors } from '../../utils';
import { User } from '../../models/user.model';
import { NewReportService } from '../../services/newReport.service';
import { FilterOperatorsEnum } from '../../models/general.models';
import { Filter } from './AOISReport/AOISReport.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { InlineSvgComponent } from '../../components/inline-svg/inline-svg.component';
import { NgClass } from '@angular/common';
import { MatFormField, MatInput } from '@angular/material/input';
import { MatButton } from '@angular/material/button';
import { MatMenuTrigger, MatMenu } from '@angular/material/menu';
import { MatTabNav, MatTabLink, MatTabNavPanel } from '@angular/material/tabs';


@Component({
  selector: 'frmg-report',
  templateUrl: './report.component.html',
  styleUrls: ['./report.component.scss'],
  imports: [InlineSvgComponent, NgClass, MatFormField, MatInput, ReactiveFormsModule, MatButton, MatMenuTrigger, MatMenu, MatTabNav, MatTabLink, MatTabNavPanel, RouterOutlet]
})
export class ReportComponent implements OnInit {
  @Input() id!: string;

  public displayedColumns = ['name', 'address__display_name', 'first_risk_score', 'first_input_point_value', 'distance', 'action'];
  public displayedColumnsDrag: string[] = [];
  public report: RiskReport;
  public colors: string[] = getRiskColors();
  public threshold: string = '0 - 200';
  public reportNameCtrl: FormControl = new FormControl<string>('');
  public editMode: boolean = false;
  public loadingData = signal(false);
  public breadCrumbs: string[];
  private allUrl = '';
  private collUrl = '';
  public depthLvl = 1;
  public currentUser: User;
  public layers: LayerData[];
  public activeLayer: string;
  public isPoint: boolean = true;
  private readonly destroyRef = inject(DestroyRef);

  constructor(private reportService: NewReportService,
              private router: Router,
              private dialog: MatDialog,
              private toastNotificationService: ToastNotificationService,
              private notificationService: NotificationService) {
    this.currentUser = JSON.parse(localStorage.getItem('user')) as User;

    this.displayedColumnsDrag = this.displayedColumns.slice(1, -1);
    this.reportService.breadCrumbs.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(resp => {
      this.breadCrumbs = resp;
    });
    this.reportService.detailsType.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(type => {
      this.isPoint = type === 'Point';
    });
    this.parseUrl();
    this.router.events.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.parseUrl();
      }
    });
  }

  public selectLayer(layer: LayerData) {
    this.activeLayer = layer.layer_name;
    this.reportService.selectedLayer.next(layer.layer_name);
  }

  private parseUrl() {
    const splits = this.router.url.split('/');
    this.allUrl = splits[2];
    const findCollectionIndex = splits.findIndex(url => url == 'collection');
    if (splits.find(url => url == 'details')) {
      this.collUrl = splits[findCollectionIndex + 1];
      this.depthLvl = 3;
    } else if (findCollectionIndex !== -1) {
      this.collUrl = splits[findCollectionIndex + 1];
      this.depthLvl = 2;
    } else {
      this.depthLvl = 1;
    }
  }

  ngOnInit() {
    this.getReport();
  }

  navigateBreadCrumbs(toCollection: boolean, skip: boolean) {
    if (skip) return;
    if (toCollection) {
      this.router.navigate([`/report/${this.allUrl}/collection/${this.collUrl}`]);
    } else {
      this.router.navigate([`/report/${this.allUrl}/`]);
    }
  }

  public deleteReport(report: RiskReport, event: Event): void {
    event.stopPropagation();
    this.dialog.open<ConfirmationDialogComponent>(ConfirmationDialogComponent, {
      width: '640px',
      data: {
        header: 'Are you sure you want to delete this report?',
        body: 'Once deleted, this report cannot be viewed.',
        confirmText: 'Delete'
      }
    }).afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(del => {
      if (del) {
        this.reportService.deleteReport(report.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
          this.goBack();
        });
      }
    });
  }

  public unShareReport(report: RiskReport, event: Event): void {
    event.stopPropagation();
    this.dialog.open<ConfirmationDialogComponent>(ConfirmationDialogComponent, {
      width: '640px',
      data: {
        header: 'Are you sure you want to remove this report from the list?',
        body: 'This report will be removed from your list. Users with access will still be able to view it.',
        confirmText: 'Yes, remove folder',
        cancelText: 'No'
      }
    }).afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(del => {
      if (del) this.reportService.leaveSharedProject().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        this.goBack();
      });
    });
  }

  private getReport(): void {
    this.loadingData.set(true);
    this.reportService.getReport(this.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(report => {
      this.report = report;
      this.isPoint = report.area_collections?.find(coll => coll.id === this.collUrl)?.geometry_json?.type === 'Point';
      this.reportNameCtrl.setValue(this.report.name || 'Report');
      this.reportService.detailsLayerData.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(resp => {
        if (this.isPoint) {
          this.layers = resp.filter(layer => layer.layer_name !== 'Exposure');
        } else this.layers = resp;
        this.layers = this.layers.sort((a, b) => {
          // @ts-ignore
          return a.layerInfo?.order - b.layerInfo?.order;
        });
        this.reportService.selectedLayer.next(this.layers[0].layer_name);
      });
      this.reportService.selectedLayer.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(resp => {
        if (this.activeLayer === resp) return;
        this.activeLayer = resp;
      });
    });
  }

  public download(id: string, filename: string = 'report'): void {
    this.reportService.getReportPdf(id, filename).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(data => {
      this.notificationService.updateDownload({
        fileName: `${filename  }.pdf`,
        percentage: data.progress,
        total: data.total,
        loaded: data.loaded
      });
    });
  }

  public downloadXLSX(id: string, filename: string = 'report'): void {
    const meta = localStorage.getItem(`report_displayedColumns_${this.id}`)?.split(',').slice(2, -1);
    let filterStr = '';
    const filters = localStorage.getItem(`${this.id}_filters`) && JSON.parse(localStorage.getItem(`${this.id}_filters`)) as Array<Filter>;
    if (filters && filters.length) {
      filters.forEach((chip, index) => {
        if (index !== 0) filterStr += '&';
        if (chip.type === FilterOperatorsEnum.RANGE) {
          filterStr += `${chip.key.name}${FilterOperatorsEnum.GTE}=${chip.value}`;
          filterStr += `&${chip.key.name}${FilterOperatorsEnum.LTE}=${chip.value2}`;
        } else {
          filterStr += `${chip.key.name}${chip.type}=${chip.value}`;
        }
      });
    }
    if (meta && meta.length) {
      filterStr += `&include_metadata=${  meta.map(m => m.replace('metadata.', '')).join(',')}`;
    }
    this.reportService.getReportXLSX(id, filename, filterStr).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(data => {
      this.notificationService.updateDownload({
        fileName: `${filename  }.xlsx`,
        percentage: data.progress,
        total: data.total,
        loaded: data.loaded
      });
    });
  }

  public goBack() {
    this.router.navigate(['/report-table']);
  }

  public share(report: RiskReport, event: Event): void {
    event.stopPropagation();
    this.dialog.open(ShareModalComponent<RiskReport>, {
      data: {
        entity: report
      }
    }).afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.reportService.getReport(this.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(report => {
        this.report.shared_with = report.shared_with;
      });
    });
  }


  public enterEditMode() {
    this.editMode = !this.editMode;
  }

  public updateReportName() {
    this.reportService.updateReport(this.report.id, { name: this.reportNameCtrl.value }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(_ => {
      this.report.name = this.reportNameCtrl.value;
      this.enterEditMode();
    }, () => {
      this.toastNotificationService.showNotification({ type: 'error', message: 'Could not update report name' });
      this.reportNameCtrl.setValue(this.report.name || 'Report');
    });
  }

  public layerLabelTransform(name: string) {
    return name && name?.replace(' Model', '')?.replace('PSBA', 'PCBA');
  }
}
